import { Box } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { CatalogItem } from 'entities/CatalogItem';
import { ItemAvailabilityStatus } from 'entities/CatalogItem/lib/itemAvailability';
import {
    CatalogItemSource,
    GlobalCategorization,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getSelectCategoryId } from 'entities/Category/model/selectors/categorySelectors';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { getSearchCatalogItemIsActive } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { getPropertyCatalogId } from 'features/fetchPropertyById/model/selectors/fetchPropertyByIdSelectors';
import { getRouteGlobalCatalog } from 'shared/const/router';
import { useIsOrdersAddItemsMarketplace } from 'shared/lib/contexts/CatalogIdentityContext';
import { useFeatureFlag } from 'shared/lib/hooks/useFeatureFlag';
import { ItemAvailabilityModal } from 'shared/ui/ItemAvailabilityModal';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';
import { CatalogItemPrice } from 'widgets/PropertyCatalog/ui/PropertyCatalogItems/PropertyCatalogItemPrice';

import { PropertyCatalogItemActionSlot } from './PropertyCatalogItemActionSlot';
import { CatalogItemCheckboxSection } from './PropertyCatalogItemCheckboxSection';
import { useExpandDetailedView } from './PropertyCatalogItemHooks';
import { PropertyCatalogItemRowActions } from './PropertyCatalogItemRowActions';
import { PropertyCatalogItemSupplierSlot } from './PropertyCatalogItemSupplierSlot';

import type { TFunction } from 'i18next';

function resolveCatalogItemSource(
    moveTo: PropertyCatalogItemsMoveTo,
    isGlobalCatalog: boolean,
    isOrderMarketplace: boolean,
): CatalogItemSource {
    const isMoveToOrderMode = moveTo === PropertyCatalogItemsMoveTo.ORDER;
    if (isMoveToOrderMode && (isGlobalCatalog || isOrderMarketplace)) {
        return 'marketplace';
    }
    return 'catalog';
}

function getItemLabel(
    t: TFunction,
    args: {
        isMoveToInventoryMode: boolean;
        isMoveToCatalogMode: boolean;
        isMoveToOrderMode: boolean;
        isInOrder?: boolean;
        isInventory: boolean;
    },
): string {
    const {
        isMoveToInventoryMode,
        isMoveToCatalogMode,
        isMoveToOrderMode,
        isInOrder,
        isInventory,
    } = args;

    const showInventoryLabel =
        (isMoveToInventoryMode ||
            isMoveToCatalogMode ||
            isMoveToOrderMode ||
            isInOrder) &&
        isInventory;

    return showInventoryLabel ? t('Inventory') : '';
}

function getCheckboxProps(
    source: CatalogItemSource,
    propertyCategoryId: string | undefined,
    globalCategorization: GlobalCategorization | undefined,
    catalogItemId: string | undefined,
): {
    categoryId?: string;
    subCategoryId?: string;
    propertyCategoryId?: string;
} {
    return {
        categoryId:
            source === 'catalog'
                ? propertyCategoryId
                : globalCategorization?.catId,
        subCategoryId:
            source === 'marketplace'
                ? globalCategorization?.subCatId
                : undefined,
        propertyCategoryId: catalogItemId ? propertyCategoryId : undefined,
    };
}

interface PropertyCatalogItemProps {
    id: string;
    selectionKey: string;
    itemUid: string;
    catalogItemId?: string;
    propertyCategoryId?: string;
    globalCategorization?: GlobalCategorization;
    idToEdit?: string;
    isInOrder?: boolean;
    isOrderPage?: boolean;
    title: string;
    thumbnailUrl: string;
    moveTo: PropertyCatalogItemsMoveTo;
    qty: number;
    isActive: boolean;
    uom: string;
    brand: string;
    qtyPerUom: number;
    quickEdit?: boolean;
    isSearch?: boolean;
    isInventory: boolean;
    price: number;
    proposals: Proposal[];
    isAvailable?: boolean;
    availabilityStatus?: ItemAvailabilityStatus;
    isAddedToCatalog?: boolean;
}

//TODO: Refactor
// Separate item by catalog items types:
// 1. Global catalog item
// 2. Property catalog item
//  2.1. Catalog item
//  2.2. Item in order

export const PropertyCatalogItem = ({
    id,
    selectionKey,
    itemUid,
    catalogItemId,
    propertyCategoryId,
    globalCategorization,
    idToEdit,
    isInOrder,
    title,
    thumbnailUrl,
    moveTo,
    qty,
    isActive,
    isOrderPage,
    uom,
    brand,
    qtyPerUom,
    quickEdit,
    isInventory,
    price,
    isSearch,
    proposals,
    isAvailable = true,
    availabilityStatus,
    isAddedToCatalog = false,
}: PropertyCatalogItemProps) => {
    const { t } = useTranslation();
    const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] =
        useState(false);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedPropertyCatalogId = useSelector(getPropertyCatalogId);
    const isCatalogItemsSearching = useSelector(getSearchCatalogItemIsActive);
    const catalogPricesVisible = useFeatureFlag('catalog_prices_is_visible');
    const canSeePrices =
        useSelector(getUserCanSeePrices) && catalogPricesVisible;
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);

    const isGlobalCatalog = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );
    const isOrderMarketplace = useIsOrdersAddItemsMarketplace();

    const isMoveToOrderMode = moveTo === PropertyCatalogItemsMoveTo.ORDER;
    const isMoveToCatalogMode = moveTo === PropertyCatalogItemsMoveTo.CATALOG;
    const isMoveToInventoryMode =
        moveTo === PropertyCatalogItemsMoveTo.INVENTORY;

    const source = resolveCatalogItemSource(
        moveTo,
        Boolean(isGlobalCatalog),
        Boolean(isOrderMarketplace),
    );

    const totalPrice =
        qty && (isOrderPage || isMoveToOrderMode) ? qty * price : 0;

    const showCheckbox = !(
        isMoveToCatalogMode &&
        isCatalogItemsSearching &&
        !isGlobalCatalog
    );

    const showPrice =
        (price || price === 0) &&
        canSeePrices &&
        !(isGlobalCatalog && !totalPrice);

    const itemLabel = getItemLabel(t, {
        isMoveToInventoryMode,
        isMoveToCatalogMode,
        isMoveToOrderMode,
        isInOrder,
        isInventory,
    });

    const checkboxCategoryProps = getCheckboxProps(
        source,
        propertyCategoryId,
        globalCategorization,
        catalogItemId,
    );

    const expandDetailedView = useExpandDetailedView({
        id,
        isSearch,
        isMoveToCatalogMode,
        isMoveToInventoryMode,
        isGlobalCatalog: !!isGlobalCatalog,
        isOrderMarketplace: !!isOrderMarketplace,
        selectedPropertyId,
        selectedCategoryId,
        selectedOrderId,
        selectedPropertyCatalogId,
    });

    const isUnavailableLocalItem =
        !isAvailable && source === 'catalog' && !isGlobalCatalog;

    const checkboxSection = showCheckbox ? (
        <CatalogItemCheckboxSection
            showQtyCheckbox={isMoveToOrderMode}
            isActive={isActive}
            qty={qty}
            itemIdToEdit={idToEdit}
            selectionKey={selectionKey}
            itemUid={itemUid}
            catalogItemId={catalogItemId}
            quickEdit={quickEdit}
            isInOrder={isInOrder}
            isMoveToInventoryMode={isMoveToInventoryMode}
            showUnavailableTooltip={
                !isAvailable &&
                (!isUnavailableLocalItem || isMoveToInventoryMode)
            }
            availabilityStatus={availabilityStatus}
            onLearnMoreActionRequired={() => setIsAvailabilityModalOpen(true)}
            isAvailable={isAvailable}
            source={source}
            {...checkboxCategoryProps}
            title={title}
        />
    ) : null;

    const useCatalogRowLayout = isMoveToCatalogMode && !isMoveToOrderMode;

    return (
        <Box
            data-testclass="item-preview"
            sx={{
                borderBottom: '1px solid',
                borderColor: 'grey.500',
                '&:hover .catalog-item-row-hover-action': {
                    opacity: 1,
                },
                ...(!isAvailable && {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                }),
            }}
        >
            <Box
                key={title}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '20px 0',
                    minHeight: '140px',
                    boxSizing: 'border-box',
                }}
            >
                <CatalogItem
                    title={title}
                    uom={uom}
                    qtyPerUom={qtyPerUom}
                    brand={brand}
                    itemLabel={itemLabel}
                    thumbnailUrl={thumbnailUrl}
                    isAvailable={isAvailable}
                    isAddedToCatalog={isAddedToCatalog}
                    onClickTitle={expandDetailedView}
                    leadingAction={
                        useCatalogRowLayout ? checkboxSection : undefined
                    }
                    supplierSlot={
                        <PropertyCatalogItemSupplierSlot
                            availabilityStatus={availabilityStatus}
                            proposals={proposals}
                            showCarriedByLabel
                            onLearnMore={
                                isMoveToInventoryMode
                                    ? undefined
                                    : () => setIsAvailabilityModalOpen(true)
                            }
                        />
                    }
                    priceSlot={
                        showPrice && (
                            <Box
                                sx={{
                                    marginTop: '12px',
                                    justifyContent: 'space-between',
                                    borderTop:
                                        showPrice || isGlobalCatalog
                                            ? '1px solid'
                                            : '',
                                    borderColor: 'grey.500',
                                }}
                            >
                                <CatalogItemPrice
                                    price={price}
                                    total={totalPrice}
                                    uom={uom}
                                />
                            </Box>
                        )
                    }
                    action={
                        useCatalogRowLayout ? (
                            <PropertyCatalogItemRowActions
                                selectionKey={selectionKey}
                                itemUid={itemUid}
                                catalogItemId={catalogItemId}
                                propertyCategoryId={propertyCategoryId}
                                title={title}
                                moveTo={moveTo}
                                showFindReplacement={
                                    isUnavailableLocalItem &&
                                    !isMoveToInventoryMode &&
                                    !isMaintenanceTechnician
                                }
                            />
                        ) : (
                            <PropertyCatalogItemActionSlot
                                showCheckbox={showCheckbox}
                                isAvailable={isAvailable}
                                availabilityStatus={availabilityStatus}
                                source={source}
                                isMoveToOrderMode={isMoveToOrderMode}
                                showQtyCheckbox={isMoveToOrderMode}
                                isActive={isActive}
                                qty={qty}
                                itemIdToEdit={idToEdit}
                                selectionKey={selectionKey}
                                itemUid={itemUid}
                                catalogItemId={catalogItemId}
                                quickEdit={quickEdit}
                                isInOrder={isInOrder}
                                {...checkboxCategoryProps}
                                title={title}
                                isGlobalCatalog={!!isGlobalCatalog}
                                isMoveToInventoryMode={isMoveToInventoryMode}
                                onLearnMoreActionRequired={() =>
                                    setIsAvailabilityModalOpen(true)
                                }
                            />
                        )
                    }
                />
            </Box>
            <ItemAvailabilityModal
                open={isAvailabilityModalOpen}
                onClose={() => setIsAvailabilityModalOpen(false)}
                copyContext={isMoveToOrderMode ? 'order' : 'propertyCatalog'}
            />
        </Box>
    );
};
